import { fetchThemeList, setIsoData } from "@utils/app";
import { Component, FormEvent } from "inferno";
import { Helmet } from "inferno-helmet";
import { CreateSite, LoginResponse, Register } from "lemmy-js-client";
import { I18NextService, UserService } from "../../services";
import {
  EMPTY_REQUEST,
  HttpService,
  LOADING_REQUEST,
  RequestState,
} from "../../services/HttpService";
import { Spinner } from "../common/icon";
import PasswordInput from "../common/password-input";
import { SiteForm } from "./site-form";
import { simpleScrollMixin } from "../mixins/scroll-mixin";
import { RouteComponentProps } from "inferno-router";
import { isBrowser } from "@utils/browser";
import { removeLocalStorageMarkdown } from "@components/common/markdown-textarea";

interface State {
  form: {
    username?: string;
    email?: string;
    password?: string;
    password_verify?: string;
    show_nsfw: boolean;
    captcha_uuid?: string;
    captcha_answer?: string;
    honeypot?: string;
    answer?: string;
  };
  doneRegisteringUser: boolean;
  registerRes: RequestState<LoginResponse>;
  themeList: string[];
}

@simpleScrollMixin
export class Setup extends Component<
  RouteComponentProps<Record<string, never>>,
  State
> {
  private isoData = setIsoData(this.context);

  state: State = {
    registerRes: EMPTY_REQUEST,
    themeList: [],
    form: {
      show_nsfw: true,
    },
    doneRegisteringUser: !!this.isoData.myUserInfo,
  };

  async componentWillMount() {
    if (isBrowser()) {
      this.setState({ themeList: await fetchThemeList() });
    }
  }

  get documentTitle(): string {
    return `${I18NextService.i18n.t("setup")} - Lemmy`;
  }

  render() {
    return (
      <div className="home-setup container-lg">
        <Helmet title={this.documentTitle} />
        <div className="row">
          <div className="col-12 offset-lg-3 col-lg-6">
            <h1 className="h4 mb-4">
              {I18NextService.i18n.t("lemmy_instance_setup")}
            </h1>
            {!this.state.doneRegisteringUser ? (
              this.registerUser()
            ) : (
              <SiteForm
                showLocal
                onCreate={form => handleCreateSite(this, form)}
                siteRes={this.isoData.siteRes}
                themeList={this.state.themeList}
                loading={false}
                myUserInfo={this.isoData.myUserInfo}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  registerUser() {
    return (
      <form onSubmit={e => handleRegisterSubmit(this, e)}>
        <h2 className="h5 mb-3">{I18NextService.i18n.t("setup_admin")}</h2>
        <div className="mb-3 row">
          <label className="col-sm-2 col-form-label" htmlFor="username">
            {I18NextService.i18n.t("username")}
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="username"
              value={this.state.form.username}
              onInput={e => handleRegisterUsernameChange(this, e)}
              required
              minLength={3}
              pattern="[a-zA-Z0-9_]+"
            />
          </div>
        </div>
        <div className="mb-3 row">
          <label className="col-sm-2 col-form-label" htmlFor="email">
            {I18NextService.i18n.t("email")}
          </label>

          <div className="col-sm-10">
            <input
              type="email"
              id="email"
              className="form-control"
              placeholder={I18NextService.i18n.t("optional")}
              value={this.state.form.email}
              onInput={e => handleRegisterEmailChange(this, e)}
              minLength={3}
            />
          </div>
        </div>
        <div className="mb-3">
          <PasswordInput
            id="password"
            value={this.state.form.password}
            onInput={e => handleRegisterPasswordChange(this, e)}
            label={I18NextService.i18n.t("password")}
            isNew
          />
        </div>
        <div className="mb-3">
          <PasswordInput
            id="verify-password"
            value={this.state.form.password_verify}
            onInput={e => handleRegisterPasswordVerifyChange(this, e)}
            label={I18NextService.i18n.t("verify_password")}
            isNew
          />
        </div>
        <div className="mb-3 row">
          <div className="col-sm-10">
            <button type="submit" className="btn btn-light border-light-subtle">
              {this.state.registerRes.state === "loading" ? (
                <Spinner />
              ) : (
                I18NextService.i18n.t("sign_up")
              )}
            </button>
          </div>
        </div>
      </form>
    );
  }
}

async function handleRegisterSubmit(
  i: Setup,
  event: FormEvent<HTMLFormElement>,
) {
  event.preventDefault();
  i.setState({ registerRes: LOADING_REQUEST });
  const {
    username,
    password_verify,
    password,
    email,
    show_nsfw,
    captcha_uuid,
    captcha_answer,
    honeypot,
    answer,
  } = i.state.form;

  if (username && password && password_verify) {
    const form: Register = {
      username,
      password,
      password_verify,
      email,
      show_nsfw,
      captcha_uuid,
      captcha_answer,
      honeypot,
      answer,
    };
    i.setState({
      registerRes: await HttpService.client.register(form),
    });

    if (i.state.registerRes.state === "success") {
      const data = i.state.registerRes.data;

      UserService.Instance.login({ res: data });
      i.setState({ doneRegisteringUser: true });
    }
  }
}

async function handleCreateSite(i: Setup, form: CreateSite) {
  const createRes = await HttpService.client.createSite(form);
  if (createRes.state === "success") {
    removeLocalStorageMarkdown();
    i.props.history.replace("/");
    location.reload();
  }
}

function handleRegisterUsernameChange(
  i: Setup,
  event: FormEvent<HTMLInputElement>,
) {
  i.state.form.username = event.target.value.trim();
  i.setState(i.state);
}

function handleRegisterEmailChange(
  i: Setup,
  event: FormEvent<HTMLInputElement>,
) {
  i.state.form.email = event.target.value;
  i.setState(i.state);
}

function handleRegisterPasswordChange(
  i: Setup,
  event: FormEvent<HTMLInputElement>,
) {
  i.state.form.password = event.target.value;
  i.setState(i.state);
}

function handleRegisterPasswordVerifyChange(
  i: Setup,
  event: FormEvent<HTMLInputElement>,
) {
  i.state.form.password_verify = event.target.value;
  i.setState(i.state);
}
